import './index.css';
import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.jsx';
import { ClerkProvider } from '@clerk/clerk-react';
import { BrowserRouter } from 'react-router-dom';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to an error reporting service
    console.error('Clerk Error Boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="max-w-md p-8 bg-white rounded-lg shadow-lg text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h2>
            <p className="mb-4">We're having trouble loading the authentication service. Please refresh the page or try again later.</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Loading Component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

// Get the publishable key from environment variables
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error("Missing Publishable Key. Please add VITE_CLERK_PUBLISHABLE_KEY to your .env file");
}

// Wrap App with ClerkProvider and error boundary
const AppWithClerk = () => {
  return (
    <ErrorBoundary>
      <ClerkProvider 
        publishableKey={PUBLISHABLE_KEY}
        appearance={{
          variables: {
            colorPrimary: '#2563eb',
            colorText: '#1f2937',
            colorBackground: '#ffffff',
            colorInputBackground: '#ffffff',
            colorInputText: '#1f2937',
          },
          elements: {
            formButtonPrimary: 'bg-blue-600 hover:bg-blue-700 text-white text-sm py-2 px-4 rounded',
            card: 'shadow-lg rounded-lg',
            headerTitle: 'text-2xl font-bold text-gray-800',
            headerSubtitle: 'text-gray-600',
            socialButtonsBlockButton: 'border border-gray-300 rounded-md',
            socialButtonsBlockButtonText: 'text-gray-700',
            dividerLine: 'bg-gray-200',
            dividerText: 'text-gray-500',
            formFieldLabel: 'text-gray-700 font-medium',
            formFieldInput: 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500',
            footerActionText: 'text-gray-600',
            footerActionLink: 'text-blue-600 hover:text-blue-800',
          },
        }}
      >
        <Suspense fallback={<LoadingSpinner />}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </Suspense>
      </ClerkProvider>
    </ErrorBoundary>
  );
};

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <AppWithClerk />
  </React.StrictMode>
);
